/**
 * Presentational chat turn matching interview modern layout (.turn / .bubble).
 * No edit/delete APIs — Office sidebar only.
 */
import React from 'react';
import { formatInterviewScrollHeader, getChatTimestampHoverTitle } from '../../../utils/formatDate';
import { getTenantInitial } from '../../../utils/brandingStyles';

export type OfficeChatTurnVariant = 'ai' | 'human';

interface OfficeChatTurnProps {
    variant: OfficeChatTurnVariant;
    senderName: string;
    timestamp?: string;
    children: React.ReactNode;
    footer?: React.ReactNode;
    /** Typing / loading state inside AI bubble */
    isTyping?: boolean;
}

function initialsFromName(name: string): string {
    const trimmed = (name || '').trim();
    if (!trimmed) return '?';
    const parts = trimmed.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return trimmed.slice(0, 2).toUpperCase();
}

const OfficeChatTurn: React.FC<OfficeChatTurnProps> = ({
    variant,
    senderName,
    timestamp,
    children,
    footer,
    isTyping = false,
}) => {
    const timeLabel = timestamp ? formatInterviewScrollHeader(timestamp) : '';
    const timeHoverTitle = timestamp
        ? getChatTimestampHoverTitle(timestamp, new Date())
        : undefined;

    const isAi = variant === 'ai';
    const avatar = isAi ? getTenantInitial() : initialsFromName(senderName);
    const displayName = (senderName || '').trim() || (isAi ? 'Famla' : '—');

    return (
        <div className={`turn ${isAi ? 'ai-turn' : 'human human-turn'}`}>
            <div className={`t-avatar ${isAi ? 't-ai' : 't-human'}`}>{avatar}</div>
            <div className="t-body">
                <div className={`t-sender ${isAi ? 's-ai' : 's-human'}`}>
                    {displayName}
                    {timeLabel && (
                        <span className="t-time" title={timeHoverTitle}>
                            {timeLabel}
                        </span>
                    )}
                </div>
                <div className="bubble-wrap">
                    <div className={`bubble${isTyping ? ' bubble--typing' : ''}`}>
                        <div className="cq-bubble-text">{children}</div>
                    </div>
                </div>
                {footer ? <div className="office-turn-footer">{footer}</div> : null}
            </div>
        </div>
    );
};

export default OfficeChatTurn;
